'use client';

import { useCallback, useRef, useState } from 'react';
import { MUTATION_KEYS, useMutationStore } from '@/shared/api';
import { generateId } from '@/shared/lib/common';
import { safeT } from '@/shared/localization';
import { useToast } from '@ncai-axe/react-ui-v3';
import { useTranslations } from 'next-intl';
import z from 'zod';
import { uploadToS3WithPutPresignedUrl } from '../api/api';
import { detectMimeType } from '../lib/image-detect';
import { IMAGE_MIN_WIDTH } from './consts';
import type { ImageItemType, UploadOptions, UploadS3ImageType } from './image.schema';
import { useImageCompression } from './useImageCompression';
type Props = {
  initialImages: ImageItemType[];
  options?: UploadOptions;
};
const CANCELED_ERROR_MESSAGES = new Set([
// 레거시 취소 메시지 호환
'Task was canceled', '이미지 업로드가 취소되었습니다.']);

// 워커/api 모듈이 throw 하는 raw 한국어 error.message 를 imageUpload 메시지 키로 매핑한다.
// 컴파일타임에 알 수 없는 런타임 문자열이므로 toast 경계에서 safeT 로 번역하고,
// 매핑에 없는 메시지(예: {limit} 가 baked-in 된 imageTooSmall)는 raw 원문으로 폴백한다.
// 워커가 {limit} 값을 보존하기 위해 던지는 파라메트릭 토큰. 한국어 문장에 숫자를
// baked-in 하면 raw→키 매핑이 miss 되므로, 워커는 이 토큰을 던지고 여기서 파싱해
// safeT 로 {limit} 보간 번역한다.
const IMAGE_TOO_SMALL_PREFIX = 'IMAGE_TOO_SMALL:';
const RAW_KO_TO_KEY: Record<string, string> = {
  '이미지 업로드가 취소되었습니다.': 'error.uploadCanceled',
  '사전 서명 URL 발급에 실패했습니다.': 'error.presignedUrlIssueFailed',
  'presigned URL 응답이 비어있습니다': 'error.presignedUrlEmpty',
  'TIFF 이미지에서 압축할 데이터를 찾지 못했습니다.': 'error.tiffNoData',
  'TIFF 이미지 크기가 유효하지 않습니다.': 'error.tiffInvalidSize',
  '이미지 미리보기 생성 중 캔버스 초기화에 실패했습니다.': 'error.thumbnailCanvasInitFailed',
  '이미지 압축 처리 중 캔버스 초기화에 실패했습니다.': 'error.compressionCanvasInitFailed',
  '이미지 처리기가 준비되지 않았습니다.': 'error.processorNotReady',
  '지원하지 않는 파일 형식입니다.': 'error.unsupportedFileType'
};
export default function useUploadToS3({
  initialImages = [],
  options = {}
}: Props) {
  const {
    mimeTypes = [],
    maxLength = 50,
    minImageWidth = IMAGE_MIN_WIDTH,
    minImageHeight,
    compressionMode,
    onValidationError
  } = options;
  const t = useTranslations('imageUpload');
  const tCommon = useTranslations('common');
  const {
    toast
  } = useToast();
  const {
    mutateAsync: issuePresignedUrl
  } = useMutationStore(MUTATION_KEYS.ISSUE_PRESIGNED_URL);
  const {
    compressImage,
    cancelCompression
  } = useImageCompression({
    compressionMode: compressionMode ?? 'reencode-when-resize-only'
  });
  const notifyValidationError = useCallback((error: Error) => {
    if (error.message.startsWith(IMAGE_TOO_SMALL_PREFIX)) {
      const limit = Number(error.message.slice(IMAGE_TOO_SMALL_PREFIX.length));
      const fallback = `${limit}px 미만의 이미지는 업로드할 수 없습니다.`;
      toast({
        text: safeT(t, 'error.imageTooSmall', fallback, {
          limit
        }),
        status: 'negative',
        duration: 3000
      });
      return;
    }
    const mappedKey = RAW_KO_TO_KEY[error.message];
    toast({
      text: safeT(t, mappedKey ?? error.message, error.message),
      status: 'negative',
      duration: 3000
    });
  }, [toast, t]);
  const handleValidationError = onValidationError ?? notifyValidationError;
  const [images, setImages] = useState<ImageItemType[]>(initialImages);
  const [isProcessing, setIsProcessing] = useState(false);
  const uploadControllersRef = useRef(new Map<string, AbortController>());
  const fileSignatureMapRef = useRef(new Map<string, string>());
  const isCanceledError = useCallback((reason: unknown): reason is Error => {
    return reason instanceof Error && CANCELED_ERROR_MESSAGES.has(reason.message);
  }, []);
  const getFileSignature = useCallback((file: File) => {
    const relativePath = file.webkitRelativePath ?? '';
    return `${relativePath}|${file.name}|${file.size}|${file.lastModified}|${file.type}`;
  }, []);
  const mimeTypeSchema = z.instanceof(File).refine((file_0: File) => {
    const fileMimeType = detectMimeType(file_0);
    if (!fileMimeType) {
      return false;
    }
    return !mimeTypes || mimeTypes.includes(fileMimeType);
  }, {
    message: t('error.unsupportedFileType')
  });
  const updateImageStatus = useCallback((id: string, updates: Partial<UploadS3ImageType>) => {
    setImages(prev => prev.some(img_0 => img_0.id === id) ? prev.map(img => img.id === id ? {
      ...img,
      ...updates
    } : img) : prev);
  }, []);
  const removeImagesByIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    setImages(prev_0 => prev_0.filter(img_1 => {
      if (!idSet.has(img_1.id)) return true;
      if (img_1.url) URL.revokeObjectURL(img_1.url);
      return false;
    }));
    ids.forEach(id_0 => fileSignatureMapRef.current.delete(id_0));
  }, []);
  const uploadToS3 = useCallback(async ({
    presignedUrl,
    file: file_1,
    id: id_1,
    key,
    signal
  }: {
    presignedUrl: string;
    file: Blob;
    id: string;
    key: string;
    signal?: AbortSignal;
  }): Promise<boolean> => {
    try {
      const success = await uploadToS3WithPutPresignedUrl({
        presignedUrl,
        file: file_1,
        signal
      });
      if (success) {
        updateImageStatus(id_1, {
          state: 'completed',
          key
        });
        return true;
      }
      return false;
    } catch (error_0) {
      if (error_0 instanceof DOMException && error_0.name === 'AbortError') {
        return false;
      }
      console.error(error_0);
      return false;
    }
  }, [updateImageStatus]);
  const uploadAllImageToS3 = useCallback(async (targetImages: UploadS3ImageType[]): Promise<{
    successCount: number;
    failCount: number;
  }> => {
    if (targetImages.length === 0) {
      return {
        successCount: 0,
        failCount: 0
      };
    }
    try {
      const results = await issuePresignedUrl({
        count: targetImages.length
      });
      const uploadResults = await Promise.allSettled(targetImages.map((targetImage, idx) => {
        const controller = new AbortController();
        uploadControllersRef.current.set(targetImage.id, controller);
        return uploadToS3({
          presignedUrl: results[idx].presigned_url,
          key: results[idx].key,
          file: targetImage.file,
          id: targetImage.id,
          signal: controller.signal
        }).finally(() => {
          uploadControllersRef.current.delete(targetImage.id);
        });
      }));
      let successCount = 0;
      const failedIds: string[] = [];
      uploadResults.forEach((result, idx_0) => {
        if (result.status === 'fulfilled' && result.value) {
          successCount++;
        } else {
          failedIds.push(targetImages[idx_0].id);
        }
      });
      removeImagesByIds(failedIds);
      return {
        successCount,
        failCount: failedIds.length
      };
    } catch (error_1) {
      console.error(error_1);
      removeImagesByIds(targetImages.map(img_2 => img_2.id));
      return {
        successCount: 0,
        failCount: targetImages.length
      };
    }
  }, [issuePresignedUrl, uploadToS3, removeImagesByIds]);
  const removeImages = useCallback((ids_0: string[]) => {
    const idSet_0 = new Set(ids_0);
    ids_0.forEach(id_2 => {
      cancelCompression(id_2);
      const controller_0 = uploadControllersRef.current.get(id_2);
      if (controller_0) {
        controller_0.abort();
        uploadControllersRef.current.delete(id_2);
      }
      fileSignatureMapRef.current.delete(id_2);
    });
    setImages(prev_1 => prev_1.filter(img_3 => {
      if (!idSet_0.has(img_3.id)) {
        return true;
      }
      if (img_3.url) {
        URL.revokeObjectURL(img_3.url);
      }
      return false;
    }));
  }, [cancelCompression]);
  const resetImages = useCallback(() => {
    uploadControllersRef.current.forEach(controller_1 => controller_1.abort());
    uploadControllersRef.current.clear();
    fileSignatureMapRef.current.clear();
    setImages(prev_2 => {
      prev_2.forEach(img_4 => {
        URL.revokeObjectURL(img_4.url ?? '');
      });
      return [];
    });
  }, []);
  const uploadImages = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setIsProcessing(true);
    const previewUrls = new Map<string, string>();
    try {
      // ── 중복 파일 체크 ──
      const existingSignatures = new Set(fileSignatureMapRef.current.values());
      const addedSignatures = new Set<string>();
      const uniqueFiles = files.filter(file_2 => {
        const signature = getFileSignature(file_2);
        if (existingSignatures.has(signature) || addedSignatures.has(signature)) {
          return false;
        }
        addedSignatures.add(signature);
        return true;
      });
      if (uniqueFiles.length !== files.length) {
        handleValidationError(new Error(t('error.duplicateFile')));
      }
      if (uniqueFiles.length === 0) {
        return;
      }

      // ── Stage 1: 로컬 검증 (확장자) ──
      // 파일 무결성은 워커의 디코드가 검증한다. 여기서 createImageBitmap 으로
      // 미리 열어보면 원본을 두 번 디코드하는 셈이고, TIFF 처럼 브라우저가
      // 디코드하지 못하는 포맷은 워커의 디코더(UTIF2)에 닿지도 못한다.
      const validFiles = uniqueFiles.filter(file_3 => mimeTypeSchema.safeParse(file_3).success);
      if (validFiles.length !== uniqueFiles.length) {
        toast({
          text: t('toast.excludedInvalidFiles'),
          status: 'positive',
          duration: 3000
        });
      }
      if (validFiles.length === 0) {
        return;
      }

      // ── Stage 2: 최대 개수 적용 후 slice ──
      const rest = maxLength - images.length;
      if (rest <= 0) {
        toast({
          text: t('toast.maxUploadReached', {
            maxLength
          }),
          status: 'positive',
          duration: 3000
        });
        return;
      }
      const hasOverflow = validFiles.length > rest;
      const filesToProcess = validFiles.slice(0, rest);
      if (hasOverflow) {
        toast({
          text: t('toast.maxUploadReached', {
            maxLength
          }),
          status: 'positive',
          duration: 3000
        });
      }
      const filesToCompress = filesToProcess.map(file_4 => {
        const id_3 = generateId();
        const signature_0 = getFileSignature(file_4);
        fileSignatureMapRef.current.set(id_3, signature_0);
        return {
          id: id_3,
          file: file_4
        };
      });

      // 고른 파일 전부를 한 번에 그리드에 올린다 — 몇 장을 넣었는지 바로 보이고,
      // 각 칸은 state:'pending' 이라 스피너가 돈다.
      //
      // url 은 비워 둔다. 102px 박스를 채우겠다고 원본 File 의 blob URL 을 걸면
      // 메인 스레드가 원본(4096x4915 기준 ~77MB)을 디코드하게 되고, 여러 장이면
      // 그만큼 프레임이 밀린다. 워커가 썸네일을 먼저 보내주면 그때 채운다.
      setImages(prev_3 => [...prev_3, ...filesToCompress.map<ImageItemType>(({
        id: id_4,
        file: file_5
      }) => ({
        id: id_4,
        url: '',
        file: file_5,
        state: 'pending'
      }))]);

      // ── Stage 3: 이미지 압축 + S3 업로드 ──
      const compressedResults = await Promise.allSettled(filesToCompress.map(({
        id: id_5,
        file: file_6
      }) => compressImage(id_5, file_6, {
        minImageWidth,
        minImageHeight,
        onPreview: thumbnail => {
          const url = URL.createObjectURL(thumbnail);
          previewUrls.set(id_5, url);
          updateImageStatus(id_5, {
            url
          });
        }
      })));
      const compressionFailedIds: string[] = [];
      const newItems: UploadS3ImageType[] = filesToCompress.map((image, index) => {
        const result_0 = compressedResults[index];
        if (result_0.status === 'rejected') {
          // 취소는 removeImages 가 이미 항목과 objectURL 을 정리했다.
          // 그 외 실패는 아래 removeImagesByIds 가 정리한다.
          if (!isCanceledError(result_0.reason)) {
            compressionFailedIds.push(image.id);
          }
          return null;
        }

        // 압축이 도는 동안 사용자가 지웠을 수 있다.
        let removed = false;
        setImages(prev_4 => {
          const foundImage = prev_4.find(imageItem => imageItem.id === image.id);
          if (!foundImage) {
            removed = true;
            return prev_4;
          }
          return prev_4.map(img_5 => img_5.id === image.id ? {
            ...img_5,
            state: 'pending',
            file: result_0.value.compressedBlob
          } : img_5);
        });
        if (removed) {
          // 항목이 사라진 뒤 도착한 미리보기는 아무도 revoke 하지 않는다.
          const orphanUrl = previewUrls.get(image.id);
          if (orphanUrl) URL.revokeObjectURL(orphanUrl);
          return null;
        }
        return {
          id: image.id,
          file: result_0.value.compressedBlob,
          url: previewUrls.get(image.id) ?? '',
          state: 'pending'
        };
      }).filter((item): item is UploadS3ImageType => item !== null);
      if (compressionFailedIds.length > 0) {
        removeImagesByIds(compressionFailedIds);
        toast({
          text: t('toast.excludedInvalidFiles'),
          status: 'positive',
          duration: 3000
        });
      }
      if (newItems.length === 0) {
        return;
      }

      // S3 업로드
      const {
        successCount: successCount_0,
        failCount: s3FailCount
      } = await uploadAllImageToS3(newItems);
      if (s3FailCount > 0) {
        if (successCount_0 > 0) {
          toast({
            text: t('toast.uploadPartialResult', {
              successCount: successCount_0,
              s3FailCount
            }),
            status: 'positive',
            duration: 3000
          });
        } else {
          toast({
            text: t('toast.uploadFailed'),
            status: 'negative',
            duration: 3000
          });
        }
      }
    } catch (error_2) {
      console.error(error_2);
      handleValidationError(new Error(tCommon('error.UNKNOWN')));
    } finally {
      setIsProcessing(false);
    }
  }, [compressImage, images, maxLength, minImageWidth, minImageHeight, toast, handleValidationError, getFileSignature, mimeTypeSchema, uploadAllImageToS3, removeImagesByIds, updateImageStatus, isCanceledError, t, tCommon]);
  const isUploadCompleted = (image_0: ImageItemType): image_0 is ImageItemType & {
    state: 'completed';
    key: string;
  } => {
    return image_0.state === 'completed' && image_0.key !== undefined && image_0.key !== '';
  };
  const reorderImages = useCallback((ids_1: string[]) => {
    setImages(prev_5 => {
      // 새로운 배열을 생성해서 React가 변경을 감지할 수 있도록 함
      const reordered = [...prev_5].sort((a, b) => {
        return ids_1.indexOf(a.id) - ids_1.indexOf(b.id);
      });
      return reordered;
    });
  }, []);
  return {
    images,
    setImages,
    uploadImages,
    uploadAllImageToS3,
    removeImages,
    isProcessing,
    isUploadCompleted,
    resetImages,
    reorderImages
  };
}